import { useState } from 'react'
import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Truck,
  MapPin,
  Navigation,
  RefreshCw,
  Loader2,
  Plus,
  X,
  CheckCircle2,
  Route as RouteIcon,
} from 'lucide-react'
import { useSessionStore } from '@/stores/sessionStore'
import { useUIStore } from '@/stores/uiStore'
import { useIsMobile } from '@/hooks'
import { useSysConfig } from '@/api/config'
import {
  useDeliveryRuns,
  useDeliveryRun,
  useCreateDeliveryRun,
  useOptimizeRun,
  useStartRun,
  useCompleteRun,
  useCancelRun,
  useRemoveOrderFromRun,
  buildRunName,
  DELIVERY_RUN_STATUS_LABEL,
  type DeliveryRun,
  type DeliveryRunStop,
  type DeliveryRunStatus,
} from '@/api/deliveryRuns'
import {
  useSetDeliveryStatus,
  useDeliveryOrders,
  DELIVERY_STATUS,
  DELIVERY_STATUS_LABEL,
  type DeliveryStatus,
} from '@/api/receipts'
import { formatCurrency } from '@/lib/utils'
import { buildStaticMapUrl } from '@/lib/staticMap'
import { setDeliveryMode } from '@/lib/deliveryMode'
import { AddToDeliveryRunModal, ConfirmModal } from '@/components/modals'
import { OrderDetailModal } from '@/components/orders/OrderDetailModal'

export const Route = createFileRoute('/deliveries')({
  beforeLoad: () => {
    const { sessionToken, selectedRegister, isAuthenticated } =
      useSessionStore.getState()
    if (!sessionToken) throw redirect({ to: '/auth' })
    if (!selectedRegister) throw redirect({ to: '/select-register' })
    if (!isAuthenticated) throw redirect({ to: '/login' })
  },
  component: DeliveriesPage,
})

function runStatusClass(status: string): string {
  switch (status) {
    case 'open':
      return 'bg-blue-100 text-blue-700'
    case 'started':
      return 'bg-amber-100 text-amber-800'
    case 'completed':
      return 'bg-green-100 text-green-800'
    case 'cancelled':
      return 'bg-gray-200 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

function deliveryStatusClass(status: string): string {
  switch (status) {
    case DELIVERY_STATUS.DELIVERED:
      return 'bg-green-100 text-green-800'
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return 'bg-amber-100 text-amber-800'
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return 'bg-red-100 text-red-700'
    case DELIVERY_STATUS.PICKUP_AT_STORE:
      return 'bg-purple-100 text-purple-700'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

function DeliveriesPage() {
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const detail = selectedRunId ? (
    <RunDetail runId={selectedRunId} onDeleted={() => setSelectedRunId(null)} />
  ) : null

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <header className="flex-none flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
        {isMobile && selectedRunId ? (
          <button
            onClick={() => setSelectedRunId(null)}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <Link
            to="/pos"
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
        )}
        <Truck className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-gray-900">
          {isMobile && selectedRunId ? 'Delivery Run' : 'Deliveries'}
        </h1>
      </header>

      {isMobile ? (
        // Mobile: stacked — the run list, tap a run for its full detail (map
        // included).
        <main className="flex-1 overflow-y-auto">
          {selectedRunId ? (
            detail
          ) : (
            <RunList selectedRunId={selectedRunId} onSelect={setSelectedRunId} />
          )}
        </main>
      ) : (
        // Desktop: 2-pane board — persistent run list ⟷ selected-run detail.
        // (A dedicated center map pane lands with the interactive all-runs map;
        // a static per-run map adds nothing over the detail's own map.)
        <main className="flex-1 flex overflow-hidden">
          <div className="w-80 flex-none overflow-y-auto border-r border-gray-200 bg-gray-50">
            <RunList selectedRunId={selectedRunId} onSelect={setSelectedRunId} />
          </div>
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {selectedRunId ? (
              detail
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                <Truck className="w-12 h-12 mb-3" />
                <p className="text-sm">
                  Select a run to view its route and stops.
                </p>
              </div>
            )}
          </div>
        </main>
      )}

      {/* Mobile: thumb-reachable Driver Mode switch anchored at the bottom,
          like the POS checkout bar (one-handed). List view only — the run
          detail has its own action bar. */}
      {isMobile && !selectedRunId && (
        <div
          className="flex-none bg-white border-t border-gray-200 px-4 pt-3"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={() => {
              setDeliveryMode(true)
              navigate({ to: '/driver' })
            }}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-lg transition-colors"
          >
            <Truck className="w-5 h-5" />
            Driver Mode
          </button>
        </div>
      )}

      {/* Standalone route: mount the global confirm dialog here so
          showConfirm() (Cancel run, etc.) actually renders — the POS
          layout that normally hosts it isn't in this route's tree. */}
      <ConfirmModal />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Run list (+ inline create)
// ---------------------------------------------------------------------------

function RunList({
  selectedRunId,
  onSelect,
}: {
  selectedRunId: number | null
  onSelect: (id: number) => void
}) {
  const { showToast } = useUIStore()
  const { selectedStore } = useSessionStore()
  // Active = the working set (open + started); History = the read-only archive
  // (completed + cancelled). The list hook already accepts a status filter.
  const [view, setView] = useState<'active' | 'history'>('active')
  const statuses: DeliveryRunStatus[] =
    view === 'active' ? ['open', 'started'] : ['completed', 'cancelled']
  const { data: runs, isLoading, refetch, isRefetching } =
    useDeliveryRuns(statuses)
  const createRun = useCreateDeliveryRun()

  const handleCreate = async () => {
    try {
      const run = await createRun.mutateAsync({
        name: buildRunName(selectedStore?.id),
      })
      onSelect(run.id)
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not create run',
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return (
    <div className="p-4 space-y-3 max-w-2xl mx-auto">
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {(['active', 'history'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${
                view === v
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
        >
          <RefreshCw
            className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      {view === 'active' && (
        <button
          onClick={handleCreate}
          disabled={createRun.isPending}
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {createRun.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          New run
        </button>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : !runs || runs.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-gray-500">
          <Truck className="w-12 h-12 mb-3 text-gray-300" />
          {view === 'active' ? (
            <>
              <p className="font-medium">No active delivery runs</p>
              <p className="text-sm">
                Create one here, or add orders from Orders → Fulfillment in the
                POS.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">No past runs</p>
              <p className="text-sm">
                Completed and cancelled runs will appear here.
              </p>
            </>
          )}
        </div>
      ) : (
        runs.map((run) => (
          <RunCard
            key={run.id}
            run={run}
            selected={run.id === selectedRunId}
            onSelect={onSelect}
          />
        ))
      )}

      {view === 'active' && <UnassignedBucket />}
    </div>
  )
}

function RunCard({
  run,
  selected,
  onSelect,
}: {
  run: DeliveryRun
  selected?: boolean
  onSelect: (id: number) => void
}) {
  return (
    <button
      onClick={() => onSelect(run.id)}
      className={`w-full text-left rounded-lg p-4 border transition-all ${
        selected
          ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500'
          : 'bg-white border-gray-200 hover:shadow-md hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-900">{run.name}</span>
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${runStatusClass(
            run.status,
          )}`}
        >
          {DELIVERY_RUN_STATUS_LABEL[run.status] ?? run.status}
        </span>
      </div>
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          {run.stop_count} stop{run.stop_count === 1 ? '' : 's'}
        </span>
        {run.driver_name && (
          <span className="flex items-center gap-1">
            <Truck className="w-4 h-4" />
            {run.driver_name}
          </span>
        )}
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Unassigned bucket — delivery orders not yet on any run
// ---------------------------------------------------------------------------

function UnassignedBucket() {
  const { data: deliveryOrders, refetch } = useDeliveryOrders()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [showModal, setShowModal] = useState(false)

  // Delivery orders not on a run yet (`delivery_run` is null). useDeliveryOrders
  // already excludes terminal (delivered / pickup) orders on the BE.
  const unassigned = (deliveryOrders ?? []).filter((o) => !o.delivery_run)
  if (unassigned.length === 0) return null

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="mt-5 pt-4 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
          Unassigned · {unassigned.length}
        </span>
        {selected.size > 0 && (
          <button
            onClick={() => setShowModal(true)}
            className="px-2.5 py-1 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Add {selected.size} to run
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {unassigned.map((o) => {
          const isSel = selected.has(o.id)
          return (
            <button
              key={o.id}
              onClick={() => toggle(o.id)}
              className={`w-full text-left rounded-lg p-2.5 border transition-colors ${
                isSel
                  ? 'bg-blue-50 border-blue-400'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs font-medium text-gray-900 truncate">
                  #{o.number ?? o.id}
                </span>
                <span className="text-xs font-medium text-gray-700">
                  {formatCurrency(Number(o.totals?.total ?? 0))}
                </span>
              </div>
              {o.customer?.display_name && (
                <div className="text-xs text-gray-600 truncate mt-0.5">
                  {o.customer.display_name}
                </div>
              )}
              {o.address && (
                <div className="text-xs text-gray-400 truncate">{o.address}</div>
              )}
            </button>
          )
        })}
      </div>
      {showModal && (
        <AddToDeliveryRunModal
          orders={unassigned.filter((o) => selected.has(o.id))}
          onClose={() => setShowModal(false)}
          onAdded={() => {
            setSelected(new Set())
            refetch()
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Run detail
// ---------------------------------------------------------------------------

function RunDetail({
  runId,
  onDeleted,
}: {
  runId: number
  onDeleted: () => void
}) {
  const { showToast, showConfirm } = useUIStore()
  const { data: run, isLoading } = useDeliveryRun(runId)
  const optimize = useOptimizeRun()
  const startRun = useStartRun()
  const completeRun = useCompleteRun()
  const cancelRunMut = useCancelRun()
  const removeOrder = useRemoveOrderFromRun()
  const setDeliveryStatus = useSetDeliveryStatus()
  // Tapping a stop opens the order detail modal (read-only review of line
  // items / payment status / notes), keyed by the stop's receipt id.
  const [openReceiptId, setOpenReceiptId] = useState<number | null>(null)

  // Browser maps key for the static-map preview (optional — the "Open in
  // Google Maps" hand-off is the primary navigation path and needs no key).
  const { data: cfg } = useSysConfig(['kiosk.delivery.google_maps_api_key'])
  const mapsKey = (cfg?.['kiosk.delivery.google_maps_api_key'] as string) || ''

  if (isLoading || !run) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  const locked = run.is_locked
  // A stop can be pulled off (and freed for another run) while the run is
  // open OR started — only completed/cancelled runs lock removal. This is
  // separate from `locked` (which still gates optimize/order-add).
  const canRemoveStops = run.status === 'open' || run.status === 'started'
  const stops = run.stops ?? []
  const mapUrl = buildStaticMapUrl(run, mapsKey)

  const act = async (
    fn: () => Promise<unknown>,
    okTitle: string,
    failTitle: string,
  ) => {
    try {
      await fn()
      showToast({ type: 'success', title: okTitle })
    } catch (err) {
      showToast({
        type: 'error',
        title: failTitle,
        message: err instanceof Error ? err.message : undefined,
      })
    }
  }

  // Destructive run actions go through a confirm modal first (the existing
  // ConfirmModal UI) so a single mis-tap on the tablet can't cancel a run,
  // lock it, or drop a stop.
  const confirmAct = (
    opts: { title: string; message: string; confirmLabel: string; variant?: 'default' | 'danger' },
    fn: () => Promise<unknown>,
    okTitle: string,
    failTitle: string,
  ) =>
    showConfirm({
      title: opts.title,
      message: opts.message,
      confirmLabel: opts.confirmLabel,
      variant: opts.variant ?? 'danger',
      onConfirm: () => act(fn, okTitle, failTitle),
    })

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto">
      {/* Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">{run.name}</h2>
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${runStatusClass(
              run.status,
            )}`}
          >
            {DELIVERY_RUN_STATUS_LABEL[run.status] ?? run.status}
          </span>
        </div>
        <p className="text-sm text-gray-600">
          {run.stop_count} stop{run.stop_count === 1 ? '' : 's'}
          {run.driver_name ? ` · Driver: ${run.driver_name}` : ''}
          {run.optimized_at ? ' · Route optimized' : ' · Not optimized'}
        </p>
      </div>

      {/* Static map preview */}
      {mapUrl && (
        <img
          src={mapUrl}
          alt="Delivery route map"
          className="w-full rounded-lg border border-gray-200"
        />
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {!locked && (
          <button
            onClick={() =>
              act(
                () => optimize.mutateAsync(run.id),
                'Route optimized',
                'Optimize failed',
              )
            }
            disabled={optimize.isPending || run.stop_count < 2}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            {optimize.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RouteIcon className="w-4 h-4" />
            )}
            Optimize route
          </button>
        )}
        {run.status === 'open' && (
          <button
            onClick={() =>
              act(
                () => startRun.mutateAsync(run.id),
                'Run started',
                'Could not start run',
              )
            }
            disabled={startRun.isPending || run.stop_count === 0}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {startRun.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Start run
          </button>
        )}
        {run.maps?.url && (
          <a
            href={run.maps.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md bg-green-600 text-white hover:bg-green-700"
          >
            <Navigation className="w-4 h-4" />
            Open in Google Maps
          </a>
        )}
        {(run.status === 'open' || run.status === 'started') && (
          <button
            onClick={() =>
              confirmAct(
                {
                  title: 'Complete run?',
                  message: 'Close this run and lock it? Stops can no longer be added or removed.',
                  confirmLabel: 'Complete run',
                  variant: 'default',
                },
                () => completeRun.mutateAsync(run.id),
                'Run completed',
                'Could not complete run',
              )
            }
            disabled={completeRun.isPending}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" />
            Complete run
          </button>
        )}
      </div>

      {run.maps?.truncated && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
          Google Maps supports up to 9 waypoints — {run.maps.dropped} stop
          {run.maps.dropped === 1 ? '' : 's'} are not in the hand-off link.
          Follow the in-app stop order for the rest.
        </p>
      )}

      {/* Stops */}
      <div className="space-y-2">
        {stops.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">
            No orders on this run yet. Add them from Orders → Fulfillment in the POS.
          </p>
        ) : (
          stops.map((stop, idx) => (
            <StopCard
              key={stop.run_order_id}
              stop={stop}
              index={idx}
              canRemove={canRemoveStops}
              onOpen={() => setOpenReceiptId(stop.receipt_id)}
              onRemove={() =>
                confirmAct(
                  {
                    title: 'Remove order?',
                    message: 'Remove this stop from the run? It stays a delivery order and can be re-added.',
                    confirmLabel: 'Remove',
                  },
                  async () => {
                    await removeOrder.mutateAsync({
                      runId: run.id,
                      runOrderId: stop.run_order_id,
                    })
                  },
                  'Order removed',
                  'Could not remove order',
                )
              }
              onSetStatus={(status) =>
                act(
                  async () => {
                    await setDeliveryStatus.mutateAsync({
                      receiptId: stop.receipt_id,
                      status,
                    })
                  },
                  'Delivery updated',
                  'Could not update delivery',
                )
              }
              busy={removeOrder.isPending || setDeliveryStatus.isPending}
            />
          ))
        )}
      </div>

      {/* Cancel (open/started) */}
      {(run.status === 'open' || run.status === 'started') && (
        <button
          onClick={() =>
            confirmAct(
              {
                title: 'Cancel run?',
                message: 'Cancel this run and detach all its orders? They stay as delivery orders you can add to another run.',
                confirmLabel: 'Cancel run',
              },
              async () => {
                await cancelRunMut.mutateAsync(run.id)
                onDeleted()
              },
              'Run cancelled',
              'Could not cancel run',
            )
          }
          disabled={cancelRunMut.isPending}
          className="w-full mt-4 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
        >
          Cancel run
        </button>
      )}

      {/* Read-only order detail for a tapped stop. */}
      {openReceiptId != null && (
        <OrderDetailModal
          receiptId={openReceiptId}
          hideAddActions
          onClose={() => setOpenReceiptId(null)}
        />
      )}
    </div>
  )
}

function StopCard({
  stop,
  index,
  canRemove,
  onOpen,
  onRemove,
  onSetStatus,
  busy,
}: {
  stop: DeliveryRunStop
  index: number
  canRemove: boolean
  onOpen: () => void
  onRemove: () => void
  onSetStatus: (status: DeliveryStatus) => void
  busy: boolean
}) {
  const seq = stop.sequence ?? index + 1
  const isTerminal =
    stop.delivery_status === DELIVERY_STATUS.DELIVERED ||
    stop.delivery_status === DELIVERY_STATUS.PICKUP_AT_STORE

  return (
    <div
      onClick={onOpen}
      className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-blue-300 transition-colors"
    >
      <div className="flex items-start gap-3">
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-semibold shrink-0">
          {seq}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-gray-900">
              #{stop.number || stop.receipt_id}
            </span>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${deliveryStatusClass(
                  stop.delivery_status,
                )}`}
              >
                {DELIVERY_STATUS_LABEL[stop.delivery_status] ??
                  stop.delivery_status}
              </span>
              {canRemove && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove()
                  }}
                  disabled={busy}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                  title="Remove from run"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {stop.customer_name && (
            <p className="text-sm text-gray-700 truncate">
              {stop.customer_name}
            </p>
          )}
          {stop.address && (
            <p className="flex items-center gap-1 text-sm text-gray-500">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                {[stop.address, stop.city, stop.province]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </p>
          )}
          {stop.total != null && (
            <p className="text-sm text-gray-600">
              {formatCurrency(Number(stop.total))}
            </p>
          )}

          {/* Per-stop delivery actions (reuse the receipts delivery-status
              endpoint). Hidden once the stop reaches a terminal state. */}
          {!isTerminal && (
            <div className="flex flex-wrap gap-2 mt-2">
              {stop.delivery_status !== DELIVERY_STATUS.OUT_FOR_DELIVERY && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onSetStatus(DELIVERY_STATUS.OUT_FOR_DELIVERY)
                  }}
                  disabled={busy}
                  className="px-2.5 py-1 text-xs font-semibold rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                >
                  Out for delivery
                </button>
              )}
              {/* No direct "Delivered" action: delivery completion runs
                  through the POS payment flow — a fully-paid delivery receipt
                  is marked delivered by the backend. Matches the deliberate
                  omission in OrderDetailModal.modalDeliveryActions. Don't
                  re-add a manual Delivered button without a paid-status guard,
                  or it bypasses collection on pay-on-delivery orders. */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onSetStatus(DELIVERY_STATUS.DELIVERY_FAILED)
                }}
                disabled={busy}
                className="px-2.5 py-1 text-xs font-semibold rounded-md bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
              >
                Failed
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

